import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useAccessControl } from "@/hooks/useAccessControl";
import { getWidgetById } from "@/utils/widgets";
import { selectTemplate } from "../templates";
import { buildLayoutExportData } from "../templates/packLayout";
import { applyLayoutData } from "../templates/applyTemplate";
import { resolveSecondTab } from "../templates/secondTab";
import {
  finishOnboardingTabs,
  lastImportedTabId,
} from "../templates/finishTabs";
import { createOwnPortfolio } from "../ownPortfolio";
import { CARD, CTA_GHOST, CTA_PRIMARY, SECTION_LABEL } from "../ui/theme";
import type { OnboardingAnswers } from "../types";

interface StepDashboardProps {
  answers: OnboardingAnswers;
  selectedWatchlistIds: number[];
  /** Called with the applied template id (or "blank") once done. */
  onComplete: (appliedTemplateId: string) => void;
}

function baseIdOf(id: string): string {
  if (getWidgetById(id)) return id;
  const dashIdx = id.indexOf("-");
  return dashIdx === -1 ? id : id.substring(0, dashIdx);
}

/**
 * Welcome flow step 3: show the template matched to the user's profile and
 * build it. Everything shown is fully usable on their plan.
 */
export default function StepDashboard({
  answers,
  selectedWatchlistIds,
  onComplete,
}: StepDashboardProps) {
  const { hasAccessToWidget } = useAccessControl();
  const [building, setBuilding] = useState(false);

  const template = useMemo(() => selectTemplate(answers), [answers]);
  /**
   * The bonus tab. Resolved for the preview as well as the build so the two
   * cannot disagree about what the user is being promised.
   */
  const secondTab = useMemo(
    () => resolveSecondTab(answers, template.id, hasAccessToWidget),
    [answers, template.id, hasAccessToWidget]
  );

  const summarise = useCallback(
    (specs: { id: string }[]) => {
      const seen = new Set<string>();
      return specs
        .map((spec) => getWidgetById(baseIdOf(spec.id)))
        .filter((w): w is NonNullable<typeof w> => !!w)
        // Only promise what this account will actually receive. applyLayoutData
        // drops widgets the plan doesn't include and re-packs around them, so an
        // unfiltered preview was the one place a trialist could be shown a widget
        // they were never going to get.
        .filter((w) => hasAccessToWidget(w))
        .filter((w) => {
          const label = w.displayName ?? w.name;
          if (seen.has(label)) return false;
          seen.add(label);
          return true;
        });
    },
    [hasAccessToWidget]
  );

  const widgetSummaries = useMemo(
    () => summarise(template.widgets),
    [summarise, template.widgets]
  );
  const secondSummaries = useMemo(
    () => (secondTab ? summarise(secondTab.widgets) : []),
    [summarise, secondTab]
  );

  const handleBuild = async () => {
    setBuilding(true);
    try {
      // Their own names first, so the primary wire can highlight them and the
      // dedicated instance can filter on them in the same layout.
      const own = await createOwnPortfolio(answers.tickers ?? []);
      const highlightWatchlistIds = own
        ? [...selectedWatchlistIds, own.id]
        : selectedWatchlistIds;
      const widgets = own
        ? [...template.widgets, own.widget]
        : template.widgets;

      const data = buildLayoutExportData(template.name, widgets);
      applyLayoutData(data, {
        hasAccessToWidget,
        highlightWatchlistIds,
        repackOnDrop: true,
      });
      const primaryTabId = lastImportedTabId();

      /**
       * The second tab is a bonus, so it is built after the first and its
       * failure is swallowed. Letting it throw here would lose the dashboard
       * the user actually asked for over a tab they never requested.
       */
      let built = 1;
      if (secondTab) {
        try {
          applyLayoutData(
            buildLayoutExportData(secondTab.name, secondTab.widgets),
            { hasAccessToWidget, highlightWatchlistIds, repackOnDrop: true }
          );
          built = 2;
        } catch (error) {
          console.error("[onboarding] second dashboard skipped:", error);
        }
      }

      // Retire the generic Home tab and land them on their own dashboard
      // rather than the bonus one.
      finishOnboardingTabs(primaryTabId);

      toast.success(
        built === 2
          ? `Your "${template.name}" and "${secondTab?.name}" dashboards are ready`
          : `Your "${template.name}" dashboard is ready`
      );
      onComplete(template.id);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to build dashboard"
      );
      setBuilding(false);
    }
  };

  return (
    <div>
      <div className={`${CARD} p-4`}>
        <div className="text-sm font-semibold text-gray-100">{template.name}</div>
        <p className="mt-1.5 text-[13px] leading-snug text-gray-400">
          {template.description}
        </p>

        <h3 className={`${SECTION_LABEL} mt-4`}>Widgets</h3>
        <ul className="mt-2 space-y-1.5">
          {widgetSummaries.map((widget) => (
            <li
              key={widget.id}
              className="flex items-start gap-2 text-[13px] leading-snug text-gray-300"
            >
              <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-squawk-blue" />
              <span>
                <span className="font-medium text-gray-100">
                  {widget.displayName ?? widget.name}
                </span>
                {widget.description && (
                  <span className="text-gray-500"> — {widget.description}</span>
                )}
              </span>
            </li>
          ))}
        </ul>

        {answers.tickers?.length ? (
          <p className="mt-4 border-t border-gray-700 pt-3 text-xs text-squawk-blue">
            Your {answers.tickers.length} tickers become a watchlist, highlighted
            across the desk wire with a feed of their own.
          </p>
        ) : null}

        {selectedWatchlistIds.length > 0 && (
          <p className="mt-4 border-t border-gray-700 pt-3 text-xs text-squawk-blue">
            {selectedWatchlistIds.length} watchlist
            {selectedWatchlistIds.length > 1 ? "s" : ""} highlighted in the wire
          </p>
        )}
      </div>

      {/* The bonus tab, shown for the same reason the first list is
          access-filtered: never promise a dashboard this account won't get. */}
      {secondTab && secondSummaries.length > 0 && (
        <div className={`${CARD} mt-3 p-4`}>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-100">
              {secondTab.name}
            </span>
            <span className="rounded border border-squawk-blue/30 bg-squawk-blue/10 px-1.5 py-0.5 text-2xs font-medium uppercase tracking-wide text-squawk-blue">
              Second tab
            </span>
          </div>
          <p className="mt-1.5 text-[13px] leading-snug text-gray-400">
            Included with your plan — {secondSummaries.length} more widgets you
            might otherwise never find.
          </p>
          <ul className="mt-3 flex flex-wrap gap-1.5">
            {secondSummaries.map((widget) => (
              <li
                key={widget.id}
                className="rounded border border-gray-700 bg-gray-800 px-1.5 py-0.5 text-xs text-gray-300"
              >
                {widget.displayName ?? widget.name}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-7 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleBuild}
          disabled={building}
          className={CTA_PRIMARY}
        >
          {building ? "Building…" : "Build my dashboard"}
        </button>
        <button
          type="button"
          onClick={() => onComplete("blank")}
          disabled={building}
          className={CTA_GHOST}
        >
          Use the default
        </button>
      </div>
    </div>
  );
}
